import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { MapGridState } from './map-grid-state';
import { MapProvider } from '../map/map-provider';
import { LayerProvider } from '../../shared/layer/layer-provider';
import { ArcGisLayerFactory } from '../../factories/arcgis-layer-factory';
import { LayerEntity } from '@gishub/domain';
import Layer from '@arcgis/core/layers/Layer';
import MapView from '@arcgis/core/views/MapView';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';

/**
 * Service/Notifier responsible for managing the state, layouts, and synchronized
 * navigation of the multi-view map grid.
 */
@Injectable()
export class MapGridNotifier extends BaseNotifier<MapGridState> {
  private static readonly INITIAL: MapGridState = {
    activeViewsCount: 1,
    rows: 1,
    cols: 1,
    syncNavigation: false,
    previewViewsCount: 9,
    previewRows: 3,
    previewCols: 3,
    focusedIndex: 0,
    views: [],
    selectedLayerIds: [],
    pickerIndex: null,
  };

  /** Instancias ArcGIS de las capas aplicadas por cada celda del grid. */
  private readonly _appliedLayers: Array<Layer[]> = [];

  /** Array of active handles (watchers, event listeners) for clean up. */
  private _watchers: Array<{ remove(): void }> = [];

  /** The MapView that currently drives the synchronized navigation. */
  private _activeView: MapView | null = null;

  /** Predefined grid configurations mapped by view count. */
  private static readonly LAYOUT_PRESETS: Record<number, { readonly rows: number; readonly cols: number }> = {
    2: { rows: 1, cols: 2 },
    4: { rows: 2, cols: 2 },
    6: { rows: 2, cols: 3 },
    9: { rows: 3, cols: 3 },
    16: { rows: 4, cols: 4 },
  };

  constructor(ref: Ref) {
    super(ref, MapGridNotifier.INITIAL);
  }

  /**
   * Updates the count, rows, and columns configurations for the preview grid.    * @param count Number of views to preview.
   */
  setPreviewViewsCount(count: number): void {
    const layout = MapGridNotifier.LAYOUT_PRESETS[count] ?? { rows: 3, cols: 3 };

    this.updateState(s => ({
      ...s,
      previewViewsCount: count,
      previewRows: layout.rows,
      previewCols: layout.cols,
    }));
  }

  /**
   * Toggles the map navigation synchronization flag and updates the watchers.
   * @param sync True to synchronize navigation, false to disable.
   */
  setSyncNavigation(sync: boolean): void {
    this.updateState(s => ({ ...s, syncNavigation: sync }));
    this.syncViews();
  }

  /**
   * Abre o cierra el selector de capas de una celda.
   * @param index Cell index whose picker should be toggled.
   */
  togglePicker(index: number): void {
    this.updateState(s => ({
      ...s,
      pickerIndex: s.pickerIndex === index ? null : index,
    }));
  }

  /**
   * Marca/desmarca una capa en la vista de una celda y aplica el cambio.
   * @param index Cell index.
   * @param layerId Layer id to toggle.
   */
  toggleLayer(index: number, layerId: string): void {
    this.updateState(s => {
      const current = s.selectedLayerIds[index] ?? [];
      const next = current.includes(layerId)
        ? current.filter(id => id !== layerId)
        : [...current, layerId];
      const nextAll = [...s.selectedLayerIds];
      nextAll[index] = next;
      return { ...s, selectedLayerIds: nextAll };
    });
    this.applyLayersToView(index);
  }

  /**
   * Limpia todas las capas activas de una celda (vuelve a solo mapa base).
   * @param index Cell index.
   */
  clearLayers(index: number): void {
    this.updateState(s => {
      const nextAll = [...s.selectedLayerIds];
      nextAll[index] = [];
      return { ...s, selectedLayerIds: nextAll };
    });
    this.applyLayersToView(index);
  }

  /**
   * Sincroniza las capas activas de la vista de una celda con las del estado:
   * agrega las marcadas y remueve las desmarcadas.
   * @param index Cell index.
   */
  private applyLayersToView(index: number): void {
    const state = this.snapshot();
    const view = state.views[index];
    if (!view) return;
    const layerIds = state.selectedLayerIds[index] ?? [];

    // Remover capas que ya no están seleccionadas
    const previous = this._appliedLayers[index] ?? [];
    previous.forEach(layer => {
      if (layer && layer.id && !layerIds.includes(layer.id) && view.map) {
        try { view.map.remove(layer); } catch {}
      }
    });
    this._appliedLayers[index] = previous.filter(
      layer => layer && layer.id && layerIds.includes(layer.id)
    );

    // Agregar las capas seleccionadas que aún no están aplicadas
    const factory = this.ref.read(ArcGisLayerFactory);
    layerIds.forEach(layerId => {
      const alreadyApplied = (this._appliedLayers[index] ?? []).some(l => l.id === layerId);
      if (alreadyApplied) return;

      const entity = this.findLayerEntityById(layerId);
      if (!entity || entity.layers) return;

      factory.createLayer(new LayerEntity({ ...entity, visible: true })).then(layer => {
        if (!layer) return;
        const currentView = this.snapshot().views[index];
        const stillSelected = (this.snapshot().selectedLayerIds[index] ?? []).includes(layerId);
        if (!currentView || currentView !== view || !currentView.map || !stillSelected) {
          try { layer.destroy(); } catch {}
          return;
        }
        currentView.map.add(layer, 0);
        this._appliedLayers[index] = [...(this._appliedLayers[index] ?? []), layer];
      });
    });
  }

  /** Busca la entidad de capa (hoja) por id dentro del árbol de LayerProvider. */
  private findLayerEntityById(id: string): LayerEntity | null {
    const layers = this.ref.read(LayerProvider).state().layers;
    const walk = (nodes: LayerEntity[]): LayerEntity | null => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.layers) {
          const found = walk(node.layers);
          if (found) return found;
        }
      }
      return null;
    };
    return walk(layers);
  }

  /**
   * Initializes and opens the multi-view map grid layout based on preview configurations.
   */
  openViews(): void {
    const s = this.snapshot();
    this.clearWatchers();
    this._appliedLayers.length = 0;
    this.updateState(state => ({
      ...state,
      activeViewsCount: s.previewViewsCount,
      rows: s.previewRows,
      cols: s.previewCols,
      views: [],
      focusedIndex: 0,
      selectedLayerIds: Array.from({ length: s.previewViewsCount }, () => []),
      pickerIndex: null,
    }));
  }

  /**
   * Closes the multi-view map grid and restores the application to a single-view map layout.
   */
  closeViews(): void {
    this.clearWatchers();
    this._appliedLayers.length = 0;
    this.updateState(s => ({
      ...s,
      activeViewsCount: 1,
      rows: 1,
      cols: 1,
      views: [],
      focusedIndex: 0,
      selectedLayerIds: [],
      pickerIndex: null,
    }));

    // Restoration of the single view of the original MapProvider will occur automatically
    // when the original MapComponent remounts.
  }

  /**
   * Registers a fully initialized MapView instance at a specific grid cell index.
   * If all active views are successfully registered, sync is automatically reconfigured.
   * @param view The ArcGIS MapView instance.
   * @param index The grid cell index where the view resides.
   */
  registerView(view: MapView, index: number): void {
    this.updateState(s => {
      const newViews = [...s.views];
      newViews[index] = view;

      // Update original MapProvider state if this view is the active/focused one
      if (index === s.focusedIndex) {
        const mapProvider = this.ref.watch(MapProvider);
        mapProvider.notifier.applyExternalState(ms => ({
          ...ms,
          view: view,
          map: view.map ?? null,
        }));
      }

      return { ...s, views: newViews };
    });

    // Reapply the layers selected for this cell once its view is ready
    if ((this.snapshot().selectedLayerIds[index] ?? []).length > 0) {
      this.applyLayersToView(index);
    }

    // Reconfigure navigation sync once all views have finished mounting
    const currentState = this.snapshot();
    if (currentState.views.filter(Boolean).length === currentState.activeViewsCount) {
      this.syncViews();
    }
  }

  /**
   * Focuses a specific map grid cell, designating it as the main active view.
   * @param index Cell index to focus.
   */
  setFocusedIndex(index: number): void {
    this.updateState(s => ({ ...s, focusedIndex: index }));
    const currentState = this.snapshot();
    const view = currentState.views[index];
    if (view) {
      const mapProvider = this.ref.watch(MapProvider);
      mapProvider.notifier.applyExternalState(ms => ({
        ...ms,
        view: view,
        map: view.map ?? null,
      }));
    }
  }

  /**
   * Synchronizes the navigation parameters (center, zoom, rotation) between all registered MapViews.
   * Implements protection against recursive feedback loop cycles by tracking the `_activeView`.
   */
  private syncViews(): void {
    this.clearWatchers();
    this._activeView = null;
    const state = this.snapshot();
    if (!state.syncNavigation || state.views.length <= 1) return;

    state.views.forEach((view, index) => {
      if (!view) return;

      // 1. Mark this view as the sync driver when focused
      const focusHandle = view.on('focus', () => {
        this._activeView = view;
      });
      this._watchers.push(focusHandle);

      // 2. Mark this view as the sync driver when actively interacted with
      const interactingHandle = reactiveUtils.watch(
        () => view.interacting,
        (interacting) => {
          if (interacting) {
            this._activeView = view;
          }
        }
      );
      this._watchers.push(interactingHandle);

      // 3. Propagate movement changes only if this view is the active sync driver
      const watchHandle = reactiveUtils.watch(
        () => [view.center, view.zoom, view.rotation],
        () => {
          if (this._activeView === view && (view.interacting || view.animation)) {
            state.views.forEach((otherView, otherIndex) => {
              if (otherView && otherIndex !== index) {
                otherView.goTo({
                  center: view.center,
                  zoom: view.zoom,
                  rotation: view.rotation,
                }, { animate: false }).catch(() => {});
              }
            });
          }
        }
      );
      this._watchers.push(watchHandle);
    });
  }

  /**
   * Removes all active event listeners and watches, and clears the active driver reference.
   */
  private clearWatchers(): void {
    this._watchers.forEach(h => {
      try { h.remove(); } catch {}
    });
    this._watchers = [];
    this._activeView = null;
  }

  /**
   * Dispose automático: libera los watchers activos al salir del viewer.
   */
  protected override onDispose(): void {
    this.clearWatchers();
    this._appliedLayers.length = 0;
  }
}

